import { useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Loader2, Sparkles, ChevronDown, Check } from 'lucide-react'
import { ThinkingEntry } from '@/stores/messageStore'

interface UnifiedThinkingIndicatorProps {
  isActive: boolean
  currentThinkingSubject?: string
  thinkingHistory: ThinkingEntry[]
  className?: string
}

/**
 * UnifiedThinkingIndicator - A unified component for showing AI processing status
 *
 * States:
 * - Processing: Job is active but no thinking subject yet (shows spinner)
 * - Thinking: Currently processing with a thinking subject (shows sparkles)
 * - History: Shows collapsible list of completed thinking steps
 *
 * Features:
 * - Smooth transitions between states
 * - Persists history even when navigating away/back
 * - Collapsible history for cleaner UI
 */
export function UnifiedThinkingIndicator({
  isActive,
  currentThinkingSubject,
  thinkingHistory,
  className
}: UnifiedThinkingIndicatorProps) {
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)
  const [animateIn, setAnimateIn] = useState(false)
  const prevSubjectRef = useRef<string | undefined>(undefined)

  // Animate when current subject changes
  useEffect(() => {
    if (currentThinkingSubject && currentThinkingSubject !== prevSubjectRef.current) {
      setAnimateIn(true)
      const timer = setTimeout(() => setAnimateIn(false), 300)
      prevSubjectRef.current = currentThinkingSubject
      return () => clearTimeout(timer)
    }
  }, [currentThinkingSubject])

  // Don't show anything if not active and no history
  if (!isActive && thinkingHistory.length === 0) {
    return null
  }

  // Filter out the current subject from history to avoid duplication
  const completedHistory = thinkingHistory.filter(
    entry => entry.completed && entry.subject !== currentThinkingSubject
  )

  // Determine the display state
  const isProcessing = isActive && !currentThinkingSubject
  const isThinking = isActive && !!currentThinkingSubject
  const hasHistory = completedHistory.length > 0

  return (
    <div className={cn(
      "rounded-lg border overflow-hidden transition-all duration-300",
      isActive
        ? "border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5"
        : "border-muted-foreground/20 bg-muted/30",
      className
    )}>
      {/* Main Status Section */}
      <div className="p-3">
        <div className="flex items-center gap-2">
          {/* Icon - changes based on state */}
          {isProcessing ? (
            <Loader2 className="h-4 w-4 text-muted-foreground animate-spin flex-shrink-0" />
          ) : isThinking ? (
            <Sparkles className={cn(
              "h-4 w-4 text-primary flex-shrink-0",
              animateIn ? "animate-pulse" : ""
            )} />
          ) : (
            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
          )}

          {/* Status Text */}
          <div className="flex-1 min-w-0">
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  AI is working...
                </span>
                {/* Animated dots */}
                <div className="flex gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            ) : isThinking ? (
              <span className={cn(
                "text-xs font-medium text-primary truncate block",
                animateIn ? "animate-fade-in" : ""
              )}>
                {currentThinkingSubject}
              </span>
            ) : (
              <span className="text-xs font-medium text-muted-foreground">
                Completed
              </span>
            )}
          </div>

          {/* History Toggle Button */}
          {hasHistory && (
            <button
              onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
              className={cn(
                "flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
                "bg-muted/50 hover:bg-muted transition-colors",
                "text-muted-foreground hover:text-foreground"
              )}
            >
              <span>{completedHistory.length} step{completedHistory.length !== 1 ? 's' : ''}</span>
              <ChevronDown className={cn(
                "h-3 w-3 transition-transform duration-200",
                isHistoryExpanded ? "rotate-180" : ""
              )} />
            </button>
          )}
        </div>
      </div>

      {/* Collapsible History Section */}
      {hasHistory && isHistoryExpanded && (
        <div className="border-t border-primary/10 bg-muted/20">
          <div className="p-2 space-y-1 max-h-32 overflow-y-auto">
            {completedHistory.map((entry) => (
              <div
                key={`${entry.subject}-${entry.timestamp}`}
                className="flex items-center gap-2 text-[11px] text-muted-foreground/80"
              >
                <Check className="h-3 w-3 text-green-500/70 flex-shrink-0" />
                <span className="truncate">{entry.subject}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
